import type { LiveBoard } from "@luckymenu/core";
import type { AlertKind } from "../adapters";

/**
 * When the staff app sounds (realtime-notifications.md, Alerting staff): a
 * QR batch waiting for a decision, or a service request nobody has
 * acknowledged, sounds until someone acts on it. Muting silences what is
 * waiting now for 5 minutes; anything new, or a repeated call from a table,
 * sounds again at once. The board comes from `listLive`, which every event
 * refetches, so the alert follows the server's state on every device.
 */

export const MUTE_MS = 5 * 60_000;

export type AlertItem = {
	/** A repeated call from the same table (a new `lastRequestedAt`) is a new item. */
	key: string;
	kind: AlertKind;
};

/** What the person can act on: batches with `batch.decide`, requests with `order.edit`. */
export type AlertRights = { decideBatches: boolean; handleRequests: boolean };

export function alertItems(board: LiveBoard, rights: AlertRights): AlertItem[] {
	const items: AlertItem[] = [];
	if (rights.decideBatches) {
		for (const b of board.pendingBatches)
			if (b.status === "pending") items.push({ key: `batch:${b.id}`, kind: "newOrder" });
	}
	if (rights.handleRequests) {
		for (const r of board.serviceRequests)
			if (r.status === "open")
				items.push({ key: `request:${r.id}:${r.lastRequestedAt}`, kind: "serviceRequest" });
	}
	return items;
}

export type AlertMute = { until: number; keys: ReadonlySet<string> };

export function muteItems(items: readonly AlertItem[], now: number): AlertMute {
	return { until: now + MUTE_MS, keys: new Set(items.map((i) => i.key)) };
}

export type AlertDecision = {
	/** What to play, or null for silence. A new order wins over a service request. */
	sound: AlertKind | null;
	/** For the banner: everything waiting, muted or not. */
	newOrders: number;
	requests: number;
	/** Some waiting items are silenced by a mute. */
	muted: boolean;
	/** When the mute ends and the decision may change, or null. */
	wakeAt: number | null;
};

export function alertDecision(
	items: readonly AlertItem[],
	mute: AlertMute | null,
	now: number,
): AlertDecision {
	const active = mute !== null && now < mute.until ? mute : null;
	const loud = active ? items.filter((i) => !active.keys.has(i.key)) : items;
	return {
		sound: loud.some((i) => i.kind === "newOrder")
			? "newOrder"
			: loud.some((i) => i.kind === "serviceRequest")
				? "serviceRequest"
				: null,
		newOrders: items.filter((i) => i.kind === "newOrder").length,
		requests: items.filter((i) => i.kind === "serviceRequest").length,
		muted: loud.length < items.length,
		wakeAt: active ? active.until : null,
	};
}
